/**
 * Service Factory - Life-and-Death Grade Dependency Injection
 *
 * This factory manages all service dependencies with proper lifecycle management,
 * error handling, and monitoring. It ensures services are properly initialized
 * and configured for critical healthcare/financial operations.
 */
import { z } from 'zod';
import { RepositoryError } from '../errors/RepositoryError';
import { success, failure } from '../types/Result';
/**
 * Service configuration schema
 */
const ServiceConfigSchema = z.object({
    // GraphQL endpoint configuration
    graphql: z.object({
        endpoint: z.string().url('GraphQL endpoint must be a valid URL'),
        timeout: z.number().positive().default(30000),
        retryAttempts: z.number().min(0).max(10).default(3),
        retryDelay: z.number().positive().default(1000)
    }),
    // Authentication configuration
    auth: z.object({
        provider: z.enum(['oauth2', 'jwt', 'saml', 'custom']),
        tokenEndpoint: z.string().url().optional(),
        refreshThreshold: z.number().positive().default(300000) // 5 minutes
    }),
    // Caching configuration
    cache: z.object({
        ttl: z.number().positive().default(300000), // 5 minutes
        maxSize: z.number().positive().default(1000),
        enabled: z.boolean().default(true)
    }),
    // Monitoring configuration
    monitoring: z.object({
        enabled: z.boolean().default(true),
        metricsEndpoint: z.string().url().optional(),
        alertingEnabled: z.boolean().default(true),
        healthCheckInterval: z.number().positive().default(60000) // 1 minute
    }),
    // Feature flags
    features: z.object({
        realTimeUpdates: z.boolean().default(true),
        offlineSupport: z.boolean().default(false),
        advancedLogging: z.boolean().default(true),
        circuitBreaker: z.boolean().default(true)
    }),
    // Environment settings
    environment: z.enum(['development', 'staging', 'production']).default('production'),
    logLevel: z.enum(['trace', 'debug', 'info', 'warn', 'error', 'fatal']).default('info')
});
/**
 * Production-grade service factory implementation
 */
export class ServiceFactory {
    constructor(config, tenantContext, logger) {
        this.services = new Map();
        this.initialized = false;
        this.initializing = false;
        this.initializationError = null;
        this.healthCheckInterval = null;
        this.serviceHealth = new Map();
        this.config = ServiceConfigSchema.parse(config);
        this.tenantContext = tenantContext;
        this.logger = logger.child({ component: 'ServiceFactory' });
    }
    /**
     * Initialize the service factory (singleton)
     */
    static async initialize(config, tenantContext, logger) {
        try {
            if (ServiceFactory.instance && ServiceFactory.instance.initialized) {
                return success(ServiceFactory.instance);
            }
            const factory = new ServiceFactory(config, tenantContext, logger);
            const initResult = await factory.initializeServices();
            if (!initResult.success) {
                const error = RepositoryError.fromUnknown(new Error(`Service initialization failed: ${initResult.failedServices.map(f => f.name).join(', ')}`), {
                    component: 'ServiceFactory',
                    operation: 'initialize',
                    metadata: { failedServices: initResult.failedServices }
                });
                return failure(error);
            }
            ServiceFactory.instance = factory;
            factory.startHealthChecks();
            factory.logger.info('Service factory initialized successfully');
            return success(factory);
        }
        catch (error) {
            const repoError = RepositoryError.fromUnknown(error, {
                component: 'ServiceFactory',
                operation: 'initialize'
            });
            return failure(repoError);
        }
    }
    /**
     * Get the singleton instance
     */
    static getInstance() {
        if (!ServiceFactory.instance || !ServiceFactory.instance.initialized) {
            throw new Error('ServiceFactory not initialized. Call ServiceFactory.initialize() first.');
        }
        return ServiceFactory.instance;
    }
    /**
     * Register a service with the factory
     */
    registerService(name, factory, options = {}) {
        if (this.services.has(name)) {
            throw new Error(`Service '${name}' is already registered`);
        }
        this.services.set(name, {
            name,
            factory,
            singleton: options.singleton ?? true,
            dependencies: options.dependencies ?? [],
            initialized: false,
            healthCheck: options.healthCheck,
            cleanup: options.cleanup
        });
        this.logger.debug(`Service registered: ${name}`);
    }
    /**
     * Get a service instance
     */
    async getService(name) {
        try {
            if (!this.initialized && !this.initializing) {
                return failure(RepositoryError.fromUnknown(new Error('ServiceFactory not initialized'), { component: 'ServiceFactory', operation: 'getService', service: name }));
            }
            const registration = this.services.get(name);
            if (!registration) {
                return failure(RepositoryError.notFound('Service', name, {
                    component: 'ServiceFactory',
                    operation: 'getService',
                    metadata: { availableServices: Array.from(this.services.keys()) }
                }));
            }
            // Return cached instance for singletons
            if (registration.singleton && registration.instance) {
                return success(registration.instance);
            }
            // Create new instance
            const instance = await this.createServiceInstance(registration);
            if (registration.singleton) {
                registration.instance = instance;
                registration.initialized = true;
            }
            return success(instance);
        }
        catch (error) {
            this.logger.error(`Failed to get service: ${name}`);
            return failure(RepositoryError.fromUnknown(error, {
                component: 'ServiceFactory',
                operation: 'getService',
                service: name
            }));
        }
    }
    /**
     * Get service synchronously (throws if not available)
     */
    getServiceSync(name) {
        if (!this.initialized) {
            throw new Error('ServiceFactory not initialized');
        }
        const registration = this.services.get(name);
        if (!registration) {
            throw new Error(`Service '${name}' not found`);
        }
        if (!registration.singleton || !registration.instance) {
            throw new Error(`Service '${name}' not available synchronously. Use getService() instead.`);
        }
        return registration.instance;
    }
    /**
     * Check if service exists and is available
     */
    hasService(name) {
        const registration = this.services.get(name);
        return registration !== undefined &&
            (!registration.singleton || registration.initialized);
    }
    /**
     * Get service health status
     */
    getServiceHealth(name) {
        return this.serviceHealth.get(name) || null;
    }
    /**
     * Get overall factory health
     */
    getFactoryHealth() {
        const services = Array.from(this.serviceHealth.values());
        const unhealthyServices = services.filter(s => !s.healthy);
        return {
            healthy: unhealthyServices.length === 0,
            services,
            unhealthyCount: unhealthyServices.length,
            lastCheck: new Date()
        };
    }
    /**
     * Initialize all registered services
     */
    async initializeServices() {
        const startTime = Date.now();
        const initializedServices = [];
        const failedServices = [];
        this.initializing = true;
        try {
            // Sort services by dependencies (topological sort)
            const sortedServices = this.topologicalSort();
            for (const serviceName of sortedServices) {
                try {
                    const registration = this.services.get(serviceName);
                    if (registration.singleton) {
                        this.logger.debug(`Initializing service: ${serviceName}`);
                        const instance = await this.createServiceInstance(registration);
                        registration.instance = instance;
                        registration.initialized = true;
                        initializedServices.push(serviceName);
                        // Initialize health status
                        this.serviceHealth.set(serviceName, {
                            name: serviceName,
                            healthy: true,
                            lastCheck: new Date()
                        });
                    }
                }
                catch (error) {
                    this.logger.error(`Failed to initialize service: ${serviceName}`);
                    failedServices.push({
                        name: serviceName,
                        error: error instanceof Error ? error.message : String(error)
                    });
                }
            }
            this.initialized = failedServices.length === 0;
            this.initializing = false;
            const totalTime = Date.now() - startTime;
            return {
                success: this.initialized,
                initializedServices,
                failedServices,
                totalTime
            };
        }
        catch (error) {
            this.initializing = false;
            this.initializationError = error instanceof Error ? error : new Error(String(error));
            return {
                success: false,
                initializedServices,
                failedServices: [...failedServices, { name: 'factory', error: String(error) }],
                totalTime: Date.now() - startTime
            };
        }
    }
    /**
     * Create service instance with dependency injection
     */
    async createServiceInstance(registration) {
        // Validate dependencies are available
        for (const depName of registration.dependencies) {
            const dep = this.services.get(depName);
            if (!dep || (dep.singleton && !dep.initialized)) {
                throw new Error(`Dependency '${depName}' not available for service '${registration.name}'`);
            }
        }
        // Create instance
        const startTime = Date.now();
        try {
            const instance = await registration.factory();
            const duration = Date.now() - startTime;
            this.logger.debug(`Service instance created: ${registration.name} in ${duration}ms`);
            return instance;
        }
        catch (error) {
            const duration = Date.now() - startTime;
            this.logger.error(`Service instance creation failed: ${registration.name}`);
            throw error;
        }
    }
    /**
     * Topological sort of services based on dependencies
     */
    topologicalSort() {
        const visited = new Set();
        const visiting = new Set();
        const result = [];
        const visit = (name) => {
            if (visiting.has(name)) {
                throw new Error(`Circular dependency detected involving service '${name}'`);
            }
            if (visited.has(name)) {
                return;
            }
            visiting.add(name);
            const registration = this.services.get(name);
            if (registration) {
                for (const dep of registration.dependencies) {
                    visit(dep);
                }
            }
            visiting.delete(name);
            visited.add(name);
            result.push(name);
        };
        for (const serviceName of this.services.keys()) {
            if (!visited.has(serviceName)) {
                visit(serviceName);
            }
        }
        return result;
    }
    /**
     * Start health check monitoring
     */
    startHealthChecks() {
        if (!this.config.monitoring.enabled) {
            return;
        }
        this.healthCheckInterval = setInterval(async () => {
            await this.performHealthChecks();
        }, this.config.monitoring.healthCheckInterval);
        this.logger.debug(`Health check monitoring started with interval: ${this.config.monitoring.healthCheckInterval}`);
    }
    /**
     * Perform health checks on all services
     */
    async performHealthChecks() {
        const checks = Array.from(this.services.entries())
            .filter(([, registration]) => registration.healthCheck && registration.initialized)
            .map(async ([name, registration]) => {
            const startTime = Date.now();
            try {
                const healthy = await registration.healthCheck();
                const responseTime = Date.now() - startTime;
                this.serviceHealth.set(name, {
                    name,
                    healthy,
                    lastCheck: new Date(),
                    responseTime
                });
                if (!healthy) {
                    this.logger.warn(`Service health check failed: ${name}`);
                }
            }
            catch (error) {
                const responseTime = Date.now() - startTime;
                this.serviceHealth.set(name, {
                    name,
                    healthy: false,
                    lastCheck: new Date(),
                    error: error instanceof Error ? error.message : String(error),
                    responseTime
                });
                this.logger.error(`Service health check error: ${name}`);
            }
        });
        await Promise.allSettled(checks);
    }
    /**
     * Graceful shutdown of all services
     */
    async shutdown() {
        this.logger.info('Starting service factory shutdown');
        // Stop health checks
        if (this.healthCheckInterval) {
            clearInterval(this.healthCheckInterval);
            this.healthCheckInterval = null;
        }
        // Cleanup services in reverse dependency order
        const sortedServices = this.topologicalSort().reverse();
        const cleanupPromises = sortedServices
            .map(name => this.services.get(name))
            .filter((registration) => registration !== undefined && registration.cleanup !== undefined && registration.initialized)
            .map(async (registration) => {
            try {
                this.logger.debug(`Cleaning up service: ${registration.name}`);
                await registration.cleanup();
                registration.initialized = false;
                registration.instance = undefined;
            }
            catch (error) {
                this.logger.error(`Service cleanup failed: ${registration.name}`);
            }
        });
        await Promise.allSettled(cleanupPromises);
        // Reset factory state
        this.initialized = false;
        this.services.clear();
        this.serviceHealth.clear();
        ServiceFactory.instance = null;
        this.logger.info('Service factory shutdown complete');
    }
    /**
     * Get factory configuration (read-only)
     */
    getConfig() {
        return { ...this.config };
    }
    /**
     * Get tenant context
     */
    getTenantContext() {
        return this.tenantContext;
    }
    /**
     * Get logger instance
     */
    getLogger() {
        return this.logger;
    }
    /**
     * Get service registry information (for debugging)
     */
    getServiceRegistry() {
        return Array.from(this.services.values()).map(registration => ({
            name: registration.name,
            initialized: registration.initialized,
            singleton: registration.singleton,
            dependencies: registration.dependencies,
            hasHealthCheck: !!registration.healthCheck
        }));
    }
}
ServiceFactory.instance = null;
//# sourceMappingURL=ServiceFactory.js.map